/**
 * One read of every part's forecast row, and the lists the daily pages are built from:
 * what to order, what to ask to ship sooner, and every delivery (PO or container) line by line.
 * Everything comes from /api/forecast (the same cache keys as the Forecast page and the
 * Dashboard), so opening any of these pages after another is instant and the numbers agree.
 *
 * Arrivals are the API's `arrivals` list: the soonest 8 per part, containers at Synkio ETA + 9
 * days, POs at their expected date. Far-off lines of a big PO can therefore be missing.
 */
import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import type { ForecastRow } from "@march/shared";
import { apiGet } from "./api.ts";
import { isYards } from "./categories.ts";
import { partName } from "./skuNames.ts";
import { getLang, locale, t } from "./i18n.tsx";

export const CATEGORIES = ["Frame", "Cover", "Base", "Fabric", "Trim", "ProtectiveCover"] as const;

export type Row = ForecastRow;

/** Said where To order leaves out the API's not_stocked parts (api/src/data/not_stocked.ts). */
export const NOT_STOCKED_NOTE = "aren't listed: Operations doesn't keep them in stock, so they aren't bought or watched here";

export interface Line {
  sku: string;
  qty: number;
  /** YYYY-MM-DD, or null when no date is set. */
  date: string | null;
  stalled: boolean;
  yards: boolean;
}

export interface Delivery {
  ref: string;
  kind: "po" | "container";
  lines: Line[];
  /** Lines due before today and not yet received (not stalled). */
  late: Line[];
  /** Over 30 days past due and never shipped (the API's stalled flag). Kept apart from late. */
  stalled: Line[];
  later: Line[];
  oldestLate: string | null;
  daysLate: number;
  /** Earliest date among the lines that aren't late. */
  next: string | null;
  units: number;
  yards: boolean;
}

export interface SpeedUp {
  ref: string;
  kind: "po" | "container";
  parts: Array<{ row: Row; emptyFrom: string; arrives: string; daysEmpty: number }>;
}

// ─── dates ──────────────────────────────────────────────────────

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const isoDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const todayIso = () => isoDay(new Date());
export const addDays = (iso: string, n: number) => {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + n);
  return isoDay(d);
};
export const daysBetween = (a: string, b: string) =>
  Math.round((new Date(b + "T12:00:00").getTime() - new Date(a + "T12:00:00").getTime()) / 86_400_000);
/** "2026-10-28" → "28 Oct" (adds the year when it isn't this year). */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return t("no date");
  const d = new Date(iso + "T12:00:00");
  const thisYear = d.getFullYear() === new Date().getFullYear();
  if (getLang() !== "en") return `${thisYear ? "" : `${d.getFullYear()}年`}${d.getMonth() + 1}月${d.getDate()}日`;
  const s = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return thisYear ? s : `${s} ${d.getFullYear()}`;
}
/** Monday of the week holding `iso`. */
export const weekStart = (iso: string) => {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return isoDay(d);
};

// ─── formatting ─────────────────────────────────────────────────

export const fmt = (n: number | null | undefined) =>
  n == null || !Number.isFinite(n) ? "—" : n !== 0 && Math.abs(n) < 1 ? n.toFixed(1) : Math.round(n).toLocaleString();
export const refLabel = (ref: string, kind: "po" | "container") => (kind === "po" ? t("PO {ref}", { ref }) : ref);
export const unitOf = (row: Row) => (isYards(row.item_type) ? "yd" : "units");

// ─── the rules ──────────────────────────────────────────────────

/** Stock that counts for the buying number: on hand plus what lands within the lead time. */
export const supplyInTime = (r: Row) => (r.supply.comp_available ?? 0) + (r.supply.incoming_within_lead ?? 0);
/** Will run out before a new order could arrive (ignoring the safety buffer). */
export const runsOut = (r: Row) => Math.round(r.lead_forecast ?? 0) - supplyInTime(r) > 0;
export const needs = (r: Row) => (r.need_to_order ?? 0) > 0;
/** Need to order is above zero only because of the safety buffer. */
export const isTopUp = (r: Row) => needs(r) && !runsOut(r);
/** Safety stock larger than the lead-time demand it protects, on a low-confidence forecast. */
export const looksHigh = (r: Row) =>
  (r.safety_qty ?? 0) - (r.lead_forecast ?? 0) >= 10 && (r.confidence === "low" || r.confidence === "low_volume");

export function buildDeliveries(rows: Row[], today: string): Map<string, Delivery> {
  const byRef = new Map<string, Delivery>();
  for (const r of rows) {
    for (const a of r.arrivals ?? []) {
      let d = byRef.get(a.ref);
      if (!d) {
        d = { ref: a.ref, kind: a.source, lines: [], late: [], stalled: [], later: [], oldestLate: null, daysLate: 0, next: null, units: 0, yards: false };
        byRef.set(a.ref, d);
      }
      d.lines.push({ sku: r.itemid, qty: a.qty, date: a.date, stalled: !!a.stalled, yards: isYards(r.item_type) });
    }
  }
  for (const d of byRef.values()) {
    d.lines.sort((x, y) => (x.date ?? "9999").localeCompare(y.date ?? "9999") || y.qty - x.qty);
    d.late = d.lines.filter((l) => l.date != null && l.date < today && !l.stalled);
    d.stalled = d.lines.filter((l) => l.date != null && l.date < today && l.stalled);
    d.later = d.lines.filter((l) => !(l.date != null && l.date < today));
    d.oldestLate = d.late[0]?.date ?? null;
    d.daysLate = d.oldestLate ? daysBetween(d.oldestLate, today) : 0;
    d.next = d.later.find((l) => l.date)?.date ?? null;
    d.yards = d.lines.every((l) => l.yards);
    d.units = d.lines.reduce((s, l) => s + l.qty, 0);
  }
  return byRef;
}

export function buildSpeedUps(rows: Row[], today: string): SpeedUp[] {
  const byRef = new Map<string, SpeedUp>();
  for (const r of rows) {
    if (!((r.days_exposed ?? 0) > 0)) continue;
    const emptyFrom = addDays(today, Math.floor(r.days_cover_on_hand ?? 0));
    const dated = (r.arrivals ?? []).filter((a) => a.date);
    const a = dated.find((x) => x.date! >= emptyFrom) ?? dated[0];
    if (!a) continue;
    let s = byRef.get(a.ref);
    if (!s) byRef.set(a.ref, (s = { ref: a.ref, kind: a.source, parts: [] }));
    s.parts.push({ row: r, emptyFrom, arrives: a.date!, daysEmpty: r.days_exposed ?? 0 });
  }
  return [...byRef.values()]
    .map((s) => ({ ...s, parts: s.parts.sort((x, y) => y.daysEmpty - x.daysEmpty) }))
    .sort((x, y) => y.parts[0].daysEmpty - x.parts[0].daysEmpty);
}

/**
 * Days the shelf would be empty over the next 200 days if delivery `ref` came `shift` days later.
 * Simple daily burn at the 90-day forecast rate; an estimate for the "what if it's late" check.
 */
export function emptyDays(row: Row, today: string, ref?: string, shift = 0): { days: number; firstEmpty: string | null } {
  const rate = (row.demand?.forecasted ?? 0) / 90;
  if (rate <= 0) return { days: 0, firstEmpty: null };
  const inflow = new Map<number, number>();
  for (const a of row.arrivals ?? []) {
    if (!a.date) continue;
    const day = Math.max(0, daysBetween(today, a.date) + (a.ref === ref ? shift : 0));
    inflow.set(day, (inflow.get(day) ?? 0) + a.qty);
  }
  let stock = row.supply.comp_available ?? 0;
  let days = 0;
  let firstEmpty: string | null = null;
  for (let day = 0; day < 200; day++) {
    stock += inflow.get(day) ?? 0;
    stock -= rate;
    if (stock <= 0) {
      days++;
      firstEmpty ??= addDays(today, day);
      stock = 0;
    }
  }
  return { days, firstEmpty };
}

// ─── search ─────────────────────────────────────────────────────

export interface SearchResult {
  parts: Row[];
  deliveries: Delivery[];
  /** A finished-umbrella code: its frame and cover, when we track them. */
  umbrella: { code: string; frame?: Row; cover?: Row; frameSku: string; coverSku: string } | null;
}

export function search(q: string, rows: Row[], deliveries: Map<string, Delivery>): SearchResult {
  const raw = q.trim();
  const upper = raw.toUpperCase().replace(/\s+/g, "");
  const res: SearchResult = { parts: [], deliveries: [], umbrella: null };
  if (!raw) return res;
  const bySku = new Map(rows.map((r) => [r.itemid, r]));

  // Finished umbrella, e.g. ALTO908002-F03 = frame ALTO908-002 + cover C908-F03.
  const fu = upper.match(/^([A-Z]+)(\d{3})(\d{3})-([A-Z]+\d+)$/);
  if (fu && !bySku.has(upper)) {
    const frameSku = `${fu[1]}${fu[2]}-${fu[3]}`;
    const coverSku = `C${fu[2]}-${fu[4]}`;
    res.umbrella = { code: upper, frameSku, coverSku, frame: bySku.get(frameSku), cover: bySku.get(coverSku) };
    return res;
  }

  // PO number or container.
  const refQ = upper.replace(/^PO/, "");
  if (/^\d{3,}$/.test(refQ) || /^CA-?\d+/.test(upper)) {
    const want = upper.startsWith("CA") ? upper.replace(/^CA-?/, "CA-") : refQ;
    for (const d of deliveries.values()) if (d.ref.toUpperCase().includes(want)) res.deliveries.push(d);
  }

  // Parts: every word must appear in the code or the name.
  const words = raw.toLowerCase().split(/\s+/).filter(Boolean);
  for (const r of rows) {
    const hay = `${r.itemid} ${partName(r.itemid)}`.toLowerCase();
    if (words.every((w) => hay.includes(w))) res.parts.push(r);
  }
  res.parts = res.parts.slice(0, 50);
  return res;
}

// ─── hooks ──────────────────────────────────────────────────────

export function useSupply() {
  const qs = useQueries({
    queries: CATEGORIES.map((t) => ({
      queryKey: ["forecast", t],
      queryFn: () => apiGet<{ rows: ForecastRow[] }>(`/api/forecast?type=${t}`),
    })),
  });
  const loading = qs.some((q) => q.isLoading);
  const error = qs.find((q) => q.error)?.error as Error | undefined;
  const dataKey = qs.map((q) => q.dataUpdatedAt).join(",");
  const today = todayIso();

  return useMemo(() => {
    const rows = qs.flatMap((q) => q.data?.rows ?? []).filter((r) => r.item_type !== "CoverBase");
    const deliveries = buildDeliveries(rows, today);
    // Parts Operations doesn't keep in stock (the API's not_stocked) stay off the buying lists; the "All" lists keep them for "Show them".
    const orderNowAll = rows.filter((r) => needs(r) && runsOut(r));
    const topUpsAll = rows.filter(isTopUp);
    const orderNow = orderNowAll.filter((r) => !r.not_stocked);
    const topUps = topUpsAll.filter((r) => !r.not_stocked);
    const speedUps = buildSpeedUps(rows, today);
    const late = [...deliveries.values()].filter((d) => d.late.length).sort((a, b) => b.daysLate - a.daysLate || a.ref.localeCompare(b.ref));
    const stalled = [...deliveries.values()].filter((d) => d.stalled.length).sort((a, b) => (a.stalled[0].date ?? "").localeCompare(b.stalled[0].date ?? ""));
    const bySku = new Map(rows.map((r) => [r.itemid, r]));
    return { loading, error, rows, bySku, deliveries, orderNow, topUps, orderNowAll, topUpsAll, speedUps, late, stalled, today };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey, loading, today]);
}

interface Ingestion {
  runs: Array<{ job_name: string; last_started: number; status: string }>;
}

/** When stock last synced from NetSuite (hourly). */
export function useFreshness() {
  const q = useQuery<Ingestion>({ queryKey: ["ingestion-health"], queryFn: () => apiGet<Ingestion>("/api/health/ingestion") });
  const at = q.data?.runs?.find((r) => r.job_name === "inventory_snapshot")?.last_started;
  if (!at) return null;
  const d = new Date(at);
  const day = `${d.toLocaleDateString(locale(), { weekday: "short" })} ${shortDate(isoDay(d))}`;
  const time = d.toLocaleTimeString(locale(), { hour: "numeric", minute: "2-digit" });
  return { day, time, label: t("Updated {day}, {time}", { day, time }) };
}

/**
 * Projected stock for each of the next `days` days: today's on-hand, minus the 90-day forecast as a
 * daily rate, plus every dated arrival on its day. Simple and explainable; it ignores order timing
 * within a day. Used by the stock timeline and the part panel's chart.
 */
export function projectStock(row: Row, today: string, days = 182): { stock: number[]; rate: number; safety: number } {
  const rate = (row.demand?.forecasted ?? 0) / 90;
  const inflow = new Map<number, number>();
  for (const a of row.arrivals ?? []) {
    if (!a.date) continue;
    const day = Math.max(0, daysBetween(today, a.date));
    inflow.set(day, (inflow.get(day) ?? 0) + a.qty);
  }
  const stock: number[] = [];
  let s = row.supply.comp_available ?? 0;
  for (let d = 0; d < days; d++) {
    s += inflow.get(d) ?? 0;
    s = Math.max(0, s - rate);
    stock.push(s);
  }
  return { stock, rate, safety: row.safety_qty ?? 0 };
}
